import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import EmailGeneral from "./Unibox/whatsappEmail/EmailGeneral";
import EmailStats from "./Unibox/whatsappEmail/EmailStats";
import { GlobalStyles, Typography, Box } from "@mui/material";
import { setCrmAuthToken } from "./crmAuthToken";

const CRM_TOKEN_LS = "token";

function syncAxiosBearer(token: string | null) {
  const t = token?.trim();
  setCrmAuthToken(t || null);
  if (t) {
    axios.defaults.headers.common.Authorization = `Bearer ${t}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
}

function AppChrome({ view }: { view: "inbox" | "stats" }) {
  return (
    <>
      <GlobalStyles
        styles={{
          ".ProseMirror-selectednode": {
            outline: "none !important",
            boxShadow: "none !important",
          },
          ".ProseMirror-node-selected": {
            outline: "none !important",
            boxShadow: "none !important",
          },
          ".ProseMirror .selectedNode": {
            outline: "none !important",
            boxShadow: "none !important",
          },
          ".ProseMirror *[style*='outline']": {
            outline: "none !important",
          },
          ".ProseMirror *[style*='border']": {
            border: "none !important",
          },
          ".ProseMirror *[style*='box-shadow']": {
            boxShadow: "none !important",
          },
          ".ProseMirror": {
            outline: "none !important",
            border: "none !important",
            minHeight: "200px",
            padding: "10px",
          },
          ".ProseMirror:focus": {
            outline: "none !important",
            border: "none !important",
          },
          ".ProseMirror:focus-visible": {
            outline: "none !important",
          },
          ".ProseMirror p + p": {
            marginTop: "2px !important",
            marginBottom: "2px !important",
          },
          ".ProseMirror p": {
            margin: "0 0 8px",
          },
        }}
      />

      <div style={{ height: "100vh", width: "100%", overflow: "hidden" }}>
        {view === "stats" ? <EmailStats /> : <EmailGeneral />}
      </div>
    </>
  );
}

export default function App() {
  const [ready, setReady] = useState(false);
  const [authError, setAuthError] = useState("");
  const fetchRestore = useRef<(() => void) | null>(null);

  const embedded = useMemo(() => {
    try {
      return (
        new URLSearchParams(window.location.search).get("embedded") === "1"
      );
    } catch {
      return false;
    }
  }, []);

  const view = useMemo(() => {
    try {
      const v = new URLSearchParams(window.location.search).get("view");
      return v === "stats" ? "stats" : "inbox";
    } catch {
      return "inbox";
    }
  }, []);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (axiosError) => {
        const s = axiosError?.response?.status;
        if (s === 401 || s === 403) {
          setAuthError("Sesión no válida con el servidor UNIBOX. Vuelve a entrar desde el CRM.");
          if (embedded) {
            window.parent?.postMessage({ type: "UNIBOX_AUTH_FAILED" }, "*");
          }
        }
        return Promise.reject(axiosError);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [embedded]);

  useEffect(() => {
    const bindFetchBearer = (token: string) => {
      if (fetchRestore.current) fetchRestore.current();
      const native = window.fetch.bind(window);
      window.fetch = async (
        input: RequestInfo | URL,
        init: RequestInit = {}
      ) => {
        const h = new Headers(init.headers ?? undefined);
        if (!h.has("Authorization")) {
          h.set("Authorization", `Bearer ${token}`);
        }
        return native(input, { ...init, headers: h });
      };
      fetchRestore.current = () => {
        window.fetch = native;
      };
    };

    if (!embedded) {
      const tok = window.localStorage.getItem(CRM_TOKEN_LS)?.trim();
      if (!tok) {
        syncAxiosBearer(null);
        setAuthError(
          "Abre UNIBOX desde el CRM (Menú herramientas → Bandeja UNIBOX) o coloca el JWT del CRM en localStorage como «token» en este mismo host/puerto (solo desarrollo)."
        );
        setReady(false);
        return undefined;
      }
      syncAxiosBearer(tok);
      bindFetchBearer(tok);
      setAuthError("");
      setReady(true);
      return () => {
        fetchRestore.current?.();
        fetchRestore.current = null;
      };
    }

    setReady(false);
    syncAxiosBearer(null);
    setAuthError("");
    fetchRestore.current?.();
    fetchRestore.current = null;

    const onMsg = (e: MessageEvent) => {
      if (e.data?.type !== "CRM_AUTH_TOKEN" || typeof e.data.token !== "string") {
        return;
      }
      const tok = e.data.token.trim();
      if (!tok) return;
      syncAxiosBearer(tok);
      bindFetchBearer(tok);
      setAuthError("");
      setReady(true);
    };

    window.addEventListener("message", onMsg);

    return () => {
      window.removeEventListener("message", onMsg);
      fetchRestore.current?.();
      fetchRestore.current = null;
    };
  }, [embedded]);

  useEffect(() => {
    function onOpenLink(e: MessageEvent) {
      if (e.data?.type !== "UNIBOX_OPEN_LINK" || typeof e.data.url !== "string") {
        return;
      }
      const url = e.data.url.trim();
      if (!url) return;

      const opened = window.open(url, "_blank", "noopener,noreferrer");
      if (!opened && embedded && window.parent !== window) {
        window.parent.postMessage({ type: "UNIBOX_OPEN_LINK", url }, "*");
      }
    }

    window.addEventListener("message", onOpenLink);
    return () => window.removeEventListener("message", onOpenLink);
  }, [embedded]);

  if (authError && !ready) {
    return (
      <Box sx={{ p: 3, maxWidth: 560 }}>
        <Typography color="error">{authError}</Typography>
      </Box>
    );
  }

  if (!ready) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Esperando token del CRM...
        </Typography>
      </Box>
    );
  }

  return <AppChrome view={view} />;
}
